/*
 *   File : list.js
 *   Author : https://evoqins.com
 *   Description : Leads list page
 *   Integrations : NA
 *   Version : 1.0.0
*/

import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'bootstrap';

//Custom components
import { PrimaryButton } from '../../Components/Buttons';
import { CustomSearchInput } from '../../Components/FormElements';
import { CustomDataTable } from '../../Components/DataTable';
import { CreateLeadModal } from '../../Components/Modal';
import { APIService } from '../../Service';
import { Loader, EmptyScreen } from '../../Components/Others';
import { toast } from 'react-toastify';

const ITEMS_PER_PAGE = 10;

const LeadsList = () => {

    const isRendered = useRef(true);
    const navigate = useNavigate();

    const [searchValue, setSearchValue] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [addLeadModal, setAddLeadModal] = useState(false);
    const [loader, setLoader] = useState(true);
    const [leadData, setLeadData] = useState({});
    const [adminList, setAdminList] = useState([]);
    const [adminLoader, setAdminLoader] = useState(true);

    const [searched, setSearched] = useState(false);

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        if (isRendered.current === true) {
            _leadList();
            isRendered.current = false
        }
    }, [currentPage, isRendered]);

    useEffect(() => {
        if (addLeadModal === true) {
            const open_modal = new Modal(document.getElementById('create-lead-modal'));
            open_modal.show();
        }
    }, [addLeadModal]);

    // handle add leads 
    const _handleAddLead = () => {
        _listAdmins();
        setAddLeadModal(true);
    };

    // handle table page change 
    const _handleTablePageChange = (page_num) => {
        isRendered.current = true;
        setCurrentPage(page_num);
    };

    // handle navigate to lead
    const _handleNavigate = (id) => {
        navigate(`/leads/${id}`);
    };

    // handle search submit  
    const _handleSearchSubmit = () => {
        _leadList();
        setSearched(true);
    };

    // handle search clear
    const _handleSearchClear = () => {
        if (searched === true) {
            _leadList("");
        }

        setSearched(false);
        setSearchValue("");
    };

    // Leads data table column 
    const TABLE_COLUMN = [
        {
            name: 'Name',
            selector: row => row.name,
            cell: (row) =>
                <span className='e-text-blue-violet text-decoration-hover-underline truncate-second-line'
                    onClick={() => _handleNavigate(row.id)}>
                    {row.name}
                </span>,
            width: "240px",
            sortable: true
        },
        {
            name: 'Email',
            cell: (row) =>
                <a href={`mailto:${row.email}`} target='_blank' className='e-text-smoky-black text-decoration-none text-decoration-hover-underline'>
                    {row.email}
                </a>,
            width: "240px"
        },
        {
            name: 'Phone number',
            cell: (row) =>
                <a href={`tel:${row.mobile}`} className='e-text-smoky-black text-decoration-none text-decoration-hover-underline'>
                    {row.mobile}
                </a>,
            width: "150px"
        },
        {
            name: 'Added date',
            selector: row => row.created,
            width: "150px",
            sortable: true,
            selector: row => new Date(row.created),  // 👈 converts string to Date object
            format: row => row.created,
        },
        {
            name: 'Assigned to',
            selector: row => row.assigned_to,
            width: "170px"
        },
        {
            name: 'Lead stage',
            selector: row => row.lead_stage_name,
            width: "140px"
        },
        {
            name: 'Last contacted date',
            selector: row => row.last_contacted_date,
            width: "160px",
            sortable: true,
            selector: row => new Date(row.last_contacted_date),  //  converts string to Date object
            format: row => row.last_contacted_date,

        },
        {
            name: 'Next follow up date',
            selector: row => row.next_follow_up_date,
            width: "160px",
            sortable: true,
            selector: row => new Date(row.next_follow_up_date),  //  converts string to Date object
            format: row => row.next_follow_up_date,
        },
    ];

    // API - Lead list
    const _leadList = (query = searchValue) => {
        let is_post = true;
        let url = 'lead/list-lead';
        let data = {
            "page_num": currentPage,
            "page_size": ITEMS_PER_PAGE,
            "query": query
        }
        setLoader(true);
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setLeadData(response.data);
            } else {
                setLeadData({ data: [] })
            }
            setLoader(false);
        });
    };

    // API - Get admins list
    const _listAdmins = () => {
        let is_post = false;
        let url = 'lead/list-admins';
        let data = {}

        setAdminLoader(true);
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                let updated_data = response.data.map(data => ({
                    value: data.id,
                    label: data.name
                }));
                setAdminList(updated_data);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setAdminLoader(false);
        });
    };


    return (
        <Fragment>
            <section className='e-section-height pb-5' >
                <div className='e-bg-lavender padding-52px-lr pt-2 padding-112px-bottom'>
                    <h3 className='mb-0 e-text-chinese-black e-primary-font e-font-24 e-font-weight-600 line-height-24px'>
                        Leads
                    </h3>
                </div>

                <div className='padding-52px-lr margin--80px-top'>
                    <div className='e-bg-white border-radius-16 padding-32px-tb '>
                        <div className='padding-32px-lr d-flex justify-content-between align-items-center'>
                            <h4 className='mb-0 e-text-smoky-black e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                                Leads list
                            </h4>
                            <div className='d-flex align-items-center gap-32px'>
                                {/* Search input */}
                                <CustomSearchInput placeholder="Search with name, email or phone number"
                                    type="text"
                                    value={searchValue}
                                    className="w-340px"
                                    onSearch={_handleSearchSubmit}
                                    onSearchClear={() => _handleSearchClear()}
                                    onClear={() => _handleSearchClear()}
                                    onKeyPress={_handleSearchSubmit}
                                    handleChange={(e) => {
                                        setSearchValue(e);
                                        if (e.length === 0) {
                                            _handleSearchClear();
                                        }
                                    }} />

                                <PrimaryButton name="Add Lead"
                                    paddingBlock="padding-12px-tb padding-42px-lr"
                                    onClick={_handleAddLead} />
                            </div>
                        </div>

                        {/* Leads list table */}
                        {loader ?
                            <Loader className="pt-5 min-vh-75" />
                            :
                            leadData.data.length !== 0 ?
                                <div className='e-data-table-wrapper-updated pt-2 mt-1'>
                                    <CustomDataTable columns={TABLE_COLUMN}
                                        data={leadData.data}
                                        lastPage={leadData.total_page}
                                        pagination={true}
                                        paginationPerPage={ITEMS_PER_PAGE}
                                        paginationTotalRows={leadData.total_count}
                                        paginationDefaultPage={leadData.current_page}
                                        onChangePage={_handleTablePageChange}
                                        rowClick={(row) => _handleNavigate(row.id)} />
                                </div>
                                :
                                <EmptyScreen width={120}
                                    height={120}
                                    className="min-vh-75"
                                    image={require('../../Assets/Image/NotFound/no-data.png')}
                                    title='No leads found'
                                />
                        }

                    </div>
                </div>
            </section>

            {/* create lead modal */}
            {addLeadModal === true &&
                <CreateLeadModal adminList={adminList}
                    loader={adminLoader}
                    handleClose={() => {
                        setTimeout(() => {
                            setAddLeadModal(false);
                        }, 200);
                    }}
                    handleSuccess={() => {
                        setTimeout(() => {
                            setAddLeadModal(false);
                            _leadList();
                        }, 200);
                    }} />
            }
        </Fragment>
    )
}

export default LeadsList;
